<template>
    <base-dialog :value="dialog" :title="$t('business.shifts_stats')" :width="760" :fullscreen="isMobile"
        @input="onInput" @close="hide">
        <div class="stats-kpis">
            <kpi-card size="sm" :label="$t('business.taken_seats')" :value="stats.taken"
                :hint="$t('business.taken_seats_hint', { n: stats.capacity })" />
            <kpi-card size="sm" :label="$t('business.occupancy')" :value="`${stats.occupancy}%`"
                :hint="$t('business.occupancy_hint')" />
            <kpi-card size="sm" :label="$t('business.full_shifts')" :value="stats.full"
                :hint="$t('business.full_shifts_hint', { n: stats.total })" />
        </div>

        <div class="stats-grid">
            <div class="stats-block">
                <span class="stats-block-title">{{ $t('business.top_slots') }}</span>
                <div v-if="stats.topSlots.length === 0" class="stats-empty">{{ $t('business.no_members_yet') }}</div>
                <div v-else class="top-list">
                    <div v-for="(slot, index) in topSlots" :key="slot.key" class="top-row">
                        <span class="top-rank">{{ index + 1 }}</span>
                        <div class="top-info">
                            <span class="top-title">{{ slot.title }}</span>
                            <span class="top-sub">{{ slot.subtitle }}</span>
                        </div>
                        <div class="top-monthly">
                            <span class="top-monthly-value">{{ slot.monthly }}</span>
                            <span class="top-monthly-label">{{ $t('business.per_month') }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="stats-block">
                <span class="stats-block-title">{{ $t('business.by_activity') }}</span>
                <horizontal-bars :items="activityItems" :empty-text="$t('business.no_members_yet')" />
            </div>

            <div class="stats-block">
                <span class="stats-block-title">{{ $t('business.by_time_slot') }}</span>
                <span class="stats-block-hint">{{ $t('business.by_time_slot_hint', { n: weeksAveraged }) }}</span>
                <div class="stats-scroll">
                    <horizontal-bars :items="timeItems" :empty-text="$t('business.no_members_yet')" />
                </div>
            </div>

            <div class="stats-block">
                <span class="stats-block-title">{{ $t('business.by_week_day') }}</span>
                <horizontal-bars :items="dayItems" :empty-text="$t('business.no_members_yet')" />
            </div>
        </div>

        <template v-slot:footer>
            <dialog-actions hide-cancel :confirm-label="$t('common.close')" @confirm="hide" />
        </template>
    </base-dialog>
</template>

<script>
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import KpiCard from "@/components/common/KpiCard";
import HorizontalBars from "@/components/common/HorizontalBars";
import { WEEK_DAYS, addWeeks, computeShiftsStats, loadShiftBookings } from "@/utils/Business";

const WEEKS_PER_MONTH = 4

const HISTORY_WEEKS = 3

const TIME_COLOR = '#66CCCC'

const DAY_COLOR = '#CCFF33'

export default {
    name: "ShiftsStatsDialog",
    components: { BaseDialog, DialogActions, KpiCard, HorizontalBars },
    props: {
        shifts: {
            type: Array,
            default: () => [],
        },
        activities: {
            type: Array,
            default: () => [],
        },
        bookings: {
            type: Object,
            default: null,
        },
        companyId: {
            type: String,
            default: null,
        },
        weekStart: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            dialog: false,
            history: [],
        }
    },
    computed: {
        stats(vm = this) {
            return computeShiftsStats(vm.shifts, vm.bookings)
        },
        topSlots(vm = this) {
            void vm.$i18n.locale
            return vm.stats.topSlots.map(slot => {
                const names = slot.activityIds.map(id => vm.activityName(id)).join(' + ')
                return {
                    key: `${slot.day}-${slot.startTime}`,
                    title: `${vm.dayName(slot.day)} ${slot.startTime}`,
                    subtitle: `${names} · ${vm.$t('business.per_week_value', { n: slot.taken })}`,
                    monthly: slot.taken * WEEKS_PER_MONTH,
                }
            })
        },
        activityItems(vm = this) {
            return Object.entries(vm.stats.byActivity)
                .map(([id, data]) => ({
                    label: vm.activityName(id),
                    value: data.taken,
                    color: vm.activityColor(id),
                    text: `${data.taken} · ${data.capacity ? Math.round(data.taken / data.capacity * 100) : 0}%`,
                }))
                .sort((a, b) => b.value - a.value)
        },
        weeksAveraged(vm = this) {
            return vm.history.length + 1
        },
        timeItems(vm = this) {
            const samples = [vm.stats.byTime, ...vm.history.map(week => computeShiftsStats(vm.shifts, week).byTime)]
            const times = new Set()
            samples.forEach(sample => Object.keys(sample).forEach(time => times.add(time)))

            return [...times].sort().map(time => {
                const total = samples.reduce((sum, sample) => sum + (sample[time] || 0), 0)
                const average = total / samples.length
                return {
                    label: time,
                    value: Math.round(average * 10) / 10,
                    color: TIME_COLOR,
                }
            })
        },
        dayItems(vm = this) {
            void vm.$i18n.locale
            return WEEK_DAYS.map(day => ({
                label: vm.dayName(day),
                value: vm.stats.byDay[day] || 0,
                color: DAY_COLOR,
            }))
        },
    },
    methods: {
        show() {
            const vm = this
            vm.dialog = true
            vm.loadHistory()
        },
        async loadHistory() {
            const vm = this
            if (!vm.companyId || !vm.weekStart) return
            vm.history = []
            try {
                const weeks = Array.from({ length: HISTORY_WEEKS }, (item, i) => addWeeks(vm.weekStart, -(i + 1)))
                vm.history = await Promise.all(weeks.map(week => loadShiftBookings(vm.companyId, week)))
            } catch (error) {
                console.error('[business] loadShiftBookings history', error?.code, error?.message)
            }
        },
        hide() {
            this.dialog = false
        },
        onInput(value) {
            if (!value) this.hide()
        },
        dayName(day) {
            return this.$moment().isoWeekday(Number(day)).format('ddd').replace('.', '')
        },
        activity(id) {
            return this.activities.find(item => item.id === id) || null
        },
        activityName(id) {
            return this.activity(id)?.name || this.$t('business.no_activity')
        },
        activityColor(id) {
            return this.activity(id)?.color || '#8892A4'
        },
    },
}
</script>

<style scoped>
.stats-kpis {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}

.stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 14px;
}

.stats-block {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 14px;
    border-radius: 10px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    min-width: 0;
}

.stats-block-title {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--v-txtMuted-base);
}

.stats-block-hint {
    margin-top: -4px;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.stats-scroll {
    display: flex;
    max-height: 220px;
    overflow-y: auto;
}

.top-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.top-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 8px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
}

.top-rank {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    border-radius: 50%;
    background: #66CCCC;
    color: #0C0E12;
    font-size: 11px;
    font-weight: 800;
}

.top-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1 1 auto;
}

.top-title {
    font-size: var(--fs-label);
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    text-transform: capitalize;
}

.top-sub {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.top-monthly {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
}

.top-monthly-value {
    font-size: 13px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
}

.top-monthly-label {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--v-txtMuted-base);
}

.stats-empty {
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

@media (max-width: 700px) {
    .stats-kpis {
        grid-template-columns: 1fr;
    }

    .stats-grid {
        grid-template-columns: 1fr;
    }
}
</style>
